import { useQueryClient } from "@tanstack/react-query";
import { Skeleton, Tabs } from "antd";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import UploadForm from "~Common/Components/UploadForm";
import { ImageTypes } from "~enums/imageTypes.ts";
import { PermissionNames } from "~enums/permissions.ts";
import type { Person } from "~types/person.ts";

type Props = {
  data?: Person;
  isLoading: boolean;
};

export const ManagePersonPhoto = ({ data, isLoading }: Props) => {
  const queryClient = useQueryClient();
  const { sport, organizationId } = useParams();
  const { formatMessage } = useIntl();

  if (isLoading || !data) return <Skeleton.Input active block style={{ width: 350, height: 480 }} />;

  const refetch = async () => {
    await queryClient.invalidateQueries({
      queryKey: ["person", sport, organizationId, data.personId],
    });
  };

  const headImage = data?.images?.find((image) => image.imageType === ImageTypes.PersonHead);

  const waistImage = data?.images?.find((image) => image.imageType === ImageTypes.PersonWaist);

  const poseImage = data?.images?.find((image) => image.imageType === ImageTypes.PersonPose);

  const items = [
    {
      key: "head",
      label: formatMessage({ id: "add.photo.PERSON.PERSON_HEAD" }),
      children: (
        <UploadForm
          imageType={ImageTypes.PersonHead}
          dataId={data.personId}
          image={headImage}
          imageFor="PERSON"
          refetch={refetch}
          permissionKey={PermissionNames.PERSON}
        />
      ),
    },
    {
      key: "waist",
      label: formatMessage({ id: "add.photo.PERSON.PERSON_WAIST" }),
      children: (
        <UploadForm
          imageType={ImageTypes.PersonWaist}
          dataId={data.personId}
          image={waistImage}
          imageFor="PERSON"
          refetch={refetch}
          permissionKey={PermissionNames.PERSON}
        />
      ),
    },
    {
      key: "posed",
      label: formatMessage({ id: "add.photo.PERSON.PERSON_POSE" }),
      children: (
        <UploadForm
          imageType={ImageTypes.PersonPose}
          dataId={data.personId}
          image={poseImage}
          imageFor="PERSON"
          refetch={refetch}
          permissionKey={PermissionNames.PERSON}
        />
      ),
    },
  ];

  return (
    <div className="card-container tabs-wrap">
      <Tabs tabBarGutter={0} items={items} />
    </div>
  );
};
